import React, { createContext, useContext, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { resolveViewedYearId } from "../shared/schoolYear";

const NavContext = createContext(null);

// Provides the single navigation "ctx" (school / year ids + the
// server-precomputed permission map) that the registry helpers consume, plus a
// navigate() that sends a resolved link to the right frontend. Mounted once high
// in the School layout so any header under it can call useNavMenu(id) to build a
// permission-filtered tab strip straight from the navigation registry - the same
// source the nav menu and search read - instead of hand-wiring a tab array.
//
// The same server-computed appProps feeds both mounts: the SPA reads it from the
// route Outlet, and the legacy-page bridge reads it from the `#app-props` blob
// Rails already renders (see chrome/pageHeaders/PageHeaderChrome). yearId comes
// from the :schoolYearId segment (SPA) or an explicit prop (bridge), else the
// staff's last selection from the legacy store, else the active school year.
//
// No nav link carries a term: a term-scoped page picks its own default term (or
// redirects to one) when it lands, so the registry never needs one and the ctx
// does not carry one.
//
// `navigate` overrides how a resolved link is dispatched. The SPA default sends a
// v2 route through react-router and a v1 route through a full page load; the
// bridge passes its own (v1 routes go through the page's guarded navigate, v2
// routes are a hard cross-app nav).
export function NavProvider({
  appProps,
  schoolSlug,
  schoolYearId = null,
  navigationMode = "spa",
  navigate: navigateOverride = null,
  children,
}) {
  const spaNavigate = useNavigate();
  const location = useLocation();

  const value = useMemo(() => {
    const yearId = resolveViewedYearId(appProps, schoolYearId);

    const ctx = {
      schoolId: appProps?.school?.id ?? null,
      schoolSlug: schoolSlug ?? appProps?.school?.slug ?? null,
      school: appProps?.school ?? null,
      permissions: appProps?.permissions ?? {},
      yearId,
    };

    const navigate =
      navigateOverride ??
      ((url, v1) => {
        if (!url) return;
        if (navigationMode === "browser") window.location.assign(url);
        else if (!v1) spaNavigate(url);
        else window.location.href = url;
      });

    const currentPath =
      navigationMode === "browser"
        ? window.location.pathname
        : location.pathname;

    return { ctx, navigate, currentPath };
  }, [
    appProps,
    schoolSlug,
    schoolYearId,
    navigationMode,
    navigateOverride,
    location.pathname,
    spaNavigate,
  ]);

  return <NavContext.Provider value={value}>{children}</NavContext.Provider>;
}

export function useNavContext() {
  return useContext(NavContext);
}
